import { useCallback, useMemo, useState } from 'react';
import { useFormState, type FieldErrors } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import {
	Button,
	Checkbox as MantineCheckbox,
	Group,
	MultiSelect,
	Radio as MantineRadio,
	Text,
} from '@mantine/core';
import { Checkbox, Textarea } from 'react-hook-form-mantine';
import {
	DocumentWorkflowTemplateInfo,
	DocumentWorkflowTemplateSourceFormat,
	DocumentWorkflowTemplateType,
	SignatureType,
} from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { getFileReactQuery } from '@dealstack/frontend/shared/dataExchange/services/mip.service.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { Tooltip } from '@dealstack/frontend/shared/components/Tooltip/Tooltip';
import {
	allowsFieldPersonalisation,
	allowsManualSignaturePlacement,
} from '@dealstack/shared/lib/common/signatures';
import { useMasterDocumentSettingsForm } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/shared/useMasterDocumentSettingsForm';
import { getFieldVisibility } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/MasterDocumentForm/useFieldVisibility';
import { useClassifierOptions } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/api/useClassifierOptions.ts';
import { CounterpartyNamesInput } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/MasterDocumentForm/CounterpartyNamesInput.tsx';
import { MasterDocumentNameField } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/MasterDocumentForm/MasterDocumentNameField.tsx';
import type { PanelHeaderEdit } from '@dealstack/frontend/shared/components/FieldBinding/TemplateNameField/templateNameField.types.ts';
import { DealFieldBindingEditor } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/MasterDocumentForm/DealFieldBindingEditor.tsx';
import { DealPdfDocumentEditor } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/MasterDocumentForm/DealPdfDocumentEditor.tsx';
import { DocumentEditorModal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/DocumentEditorModal/DocumentEditorModal.tsx';
import {
	MASTER_DOCUMENT_FORM_FIELD,
	MasterDocumentFormDto,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/MasterDocumentForm/types.ts';
import { getSignatureType } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/utils/getSignatureType.ts';
import { formatEnumValue } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/utils/formatEnumValue.ts';
import { scheduleCouplingDisabled } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/utils/scheduleCouplingDisabled.ts';
import { getDocumentEditorTabs } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentsModal/components/documentRowEditor.utils';
import { DocumentBindingCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentsModal/components/DocumentBindingCard';
import {
	Column,
	ColumnsGrid,
	ColumnSubSection,
	ColumnSubSectionBody,
	ColumnSubSectionHeader,
	EditorPanel,
	FooterBar,
	DependentField,
	RowEditorContainer,
	SectionHeaderCell,
	SectionHeaderRow,
	WarningBanner,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentsModal/components/documentRowEditor.styled.ts';

interface Props {
	template: DocumentWorkflowTemplateInfo;
}

/**
 * Inline settings editor for an expanded row in the "Send documents" picker. Every edit here
 * writes through to the Master Document itself (via {@link useMasterDocumentSettingsForm});
 * nothing on this panel is scoped to the one send.
 */
export const DocumentRowEditor = ({ template }: Props) => {
	const tMipManage = useTranslationMipManage();
	const { form, save, isSaving } = useMasterDocumentSettingsForm(template);

	const [isEditorOpen, setIsEditorOpen] = useState(false);
	// `name`-scoped so a keystroke in the rename does not re-render every control on the panel.
	const { dirtyFields: nameDirtyFields } = useFormState({
		control: form.control,
		name: MASTER_DOCUMENT_FORM_FIELD.name,
	});
	const documentType = form.watch(MASTER_DOCUMENT_FORM_FIELD.type);
	const signatureTypeValue = form.watch(MASTER_DOCUMENT_FORM_FIELD.signatureType);
	const individualInstances = form.watch(MASTER_DOCUMENT_FORM_FIELD.individualInstances) ?? true;
	const classifiersValue = form.watch(MASTER_DOCUMENT_FORM_FIELD.classifiers) ?? [];

	const visibility = getFieldVisibility({
		type: documentType,
		signatureType: signatureTypeValue,
		individualInstances,
	});

	const signatureRequired =
		documentType === DocumentWorkflowTemplateType.SIGNATURE_REQUIRED ||
		documentType === DocumentWorkflowTemplateType.SIGNABLE_FORM;
	const toBeSubmitted =
		documentType === DocumentWorkflowTemplateType.FORM_INPUT ||
		documentType === DocumentWorkflowTemplateType.SIGNABLE_FORM;

	/**
	 * Fires only when `form.handleSubmit` finds the form invalid — never for a rejection `save()`
	 * itself throws, which is why this can toast unconditionally without colliding with
	 * `useUpdateMasterDocument`'s own `onError` toast for a backend refusal.
	 */
	const handleInvalid = useCallback(
		(errors: FieldErrors<MasterDocumentFormDto>) => {
			const firstFieldKey = Object.keys(errors)[0] as
				| keyof FieldErrors<MasterDocumentFormDto>
				| undefined;
			const firstMessage = firstFieldKey
				? (errors[firstFieldKey] as { message?: string } | undefined)?.message
				: undefined;
			showToastV2({
				state: 'error',
				title: firstMessage ?? tMipManage('send_documents.row_editor.toast.invalid'),
			});
		},
		[tMipManage]
	);

	/**
	 * `save` validates once and calls `handleInvalid` in place of the write, so there is no outer
	 * handleSubmit here doing the same validation a second time. Anything it rejects with is a
	 * backend refusal, already toasted by `useUpdateMasterDocument`'s `onError`.
	 */
	const handleSave = useCallback(async () => {
		try {
			await save(handleInvalid);
		} catch {
			// Backend failures are already toasted by the update mutation's own onError.
		}
	}, [save, handleInvalid]);

	const fileId = template.file?.id ?? null;
	const { data: templateFile } = useQuery({
		queryKey: [API_PATHS.FILE_ID_GET, { id: fileId }, 'send-documents-row-editor'],
		queryFn: async ({ signal }) => {
			if (!fileId) {
				return undefined;
			}
			const response = await getFileReactQuery({ id: fileId }, signal);
			return response ? await response.blob() : undefined;
		},
		enabled: !!fileId,
	});

	const isDocx = template.sourceFormat === DocumentWorkflowTemplateSourceFormat.DOCX;
	const isMultiSign = individualInstances === false;
	const canPlaceSignaturesManually = allowsManualSignaturePlacement({
		signatureType: getSignatureType({
			type: documentType,
			formSignatureType: signatureTypeValue,
		}),
		isMultiSign,
	});
	const canPersonaliseFields = allowsFieldPersonalisation({ isMultiSign });
	const postDatingEnabled = form.watch(MASTER_DOCUMENT_FORM_FIELD.requiresPostDating) ?? false;

	const placementLockReason =
		template.settingLocks.useManualSignaturePlacement.reason ??
		template.settingLocks.signaturePlacements.reason;
	const { showFieldsTab, showSignaturesTab, hasWork } = getDocumentEditorTabs({
		isDocx,
		canPlaceSignaturesManually,
		canPersonaliseFields,
		postDatingEnabled,
	});
	const bindingDisabled = isSaving || !templateFile;

	const witnessLocked = template.settingLocks.witnessRequired.locked;
	const signatureRequiredLocked = template.settingLocks.requiresSignature.locked;

	const counterpartyWitnessAvailable = signatureTypeValue !== SignatureType.WET_INK;
	const individualScheduleDisabled = scheduleCouplingDisabled({ individualInstances });

	const classifierOptions = useClassifierOptions();
	// `classifiersValue` (form.watch) is a fresh array every render and would defeat the memo, so
	// the options list is derived only from the catalogue plus this document's own existing tags —
	// the current selection is passed separately as `value`, below.
	const classifierData = useMemo(() => {
		const knownValues = new Set(classifierOptions.map((option) => option.value));
		const existingOnly = (template.classifiers ?? []).filter((name) => !knownValues.has(name));
		return [
			...classifierOptions,
			...existingOnly.map((name) => ({ value: name, label: name })),
		];
	}, [classifierOptions, template.classifiers]);

	// Figma lays the methods out side by side, not stacked. Locked like the checkboxes above:
	// the signing method is part of the config settled when the document was created.
	const lockedReason = signatureRequiredLocked
		? formatEnumValue(template.settingLocks.requiresSignature.reason)
		: tMipManage('master_documents.tooltip.type_locked_after_creation');
	const signatureMethodOptions = (
		<Group gap="lg">
			<MantineRadio
				value={SignatureType.ELECTRONIC}
				label={tMipManage('send_documents.signature.electronic')}
				disabled
			/>
			<MantineRadio
				value={SignatureType.WET_INK}
				label={tMipManage('send_documents.row_editor.signature_wet_ink')}
				disabled
			/>
		</Group>
	);

	const closeEditor = () => setIsEditorOpen(false);

	// The editor's side panel renames the document, mirroring MasterDocumentForm: the field edits
	// this form rather than writing on its own, so the panel's Save runs the same handleSave and
	// the two surfaces can never hold different names.
	const documentNameField = (
		<MasterDocumentNameField control={form.control} disabled={isSaving} />
	);
	const documentNameEdit: PanelHeaderEdit = {
		isDirty: !!nameDirtyFields.name,
		save: () => void handleSave(),
		discard: () => form.resetField(MASTER_DOCUMENT_FORM_FIELD.name),
		isSaving,
	};

	return (
		<RowEditorContainer>
			<EditorPanel>
				<SectionHeaderRow>
					<SectionHeaderCell>
						<Text fw={600}>
							{tMipManage('send_documents.row_editor.section.requirement')}
						</Text>
					</SectionHeaderCell>
					<SectionHeaderCell>
						<Text fw={600}>
							{tMipManage('send_documents.row_editor.section.personalised_document')}
						</Text>
					</SectionHeaderCell>
				</SectionHeaderRow>
				<ColumnsGrid>
					<Column data-testid="send-documents-requirement-column">
						{/* The whole signing setup reads the document's `type` and signing
						    config, both fixed at creation: the update endpoint carries no `type`
						    field and the service writes it only in createTemplate. So these
						    report the document rather than change it, the way the Master
						    Documents drawer disables its type Select. The reason is a tooltip
						    rather than helper text so it does not push the group apart. */}
						<Tooltip tooltip={lockedReason} hoverChildrenInsteadIcon>
							<MantineCheckbox
								label={tMipManage('send_documents.row_editor.signature_required')}
								checked={signatureRequired}
								disabled
							/>
						</Tooltip>
						{/* Directly under the checkbox it belongs to, and greyed rather than
						    removed, so the column does not reflow. */}
						<DependentField>
							<MantineRadio.Group
								label={tMipManage('send_documents.row_editor.signature_method')}
								value={signatureTypeValue ?? undefined}
								onChange={() => undefined}
							>
								{signatureMethodOptions}
							</MantineRadio.Group>
						</DependentField>
						{/* Reports the other half of the document's type, so it sits with its
						    twin rather than under Delivery. */}
						<Tooltip tooltip={lockedReason} hoverChildrenInsteadIcon>
							<MantineCheckbox
								label={tMipManage('send_documents.delivery.to_be_submitted')}
								checked={toBeSubmitted}
								disabled
							/>
						</Tooltip>
						{visibility.witnessRequired.show && (
							<Checkbox
								name={MASTER_DOCUMENT_FORM_FIELD.witnessRequired}
								control={form.control}
								label={tMipManage('send_documents.row_editor.witnessing')}
								disabled={isSaving || witnessLocked}
								description={
									witnessLocked
										? formatEnumValue(
												template.settingLocks.witnessRequired.reason
											)
										: undefined
								}
							/>
						)}
						{visibility.counterparties.show && (
							<CounterpartyNamesInput
								value={form.watch(MASTER_DOCUMENT_FORM_FIELD.counterparties) ?? []}
								onChange={(value) =>
									form.setValue(
										MASTER_DOCUMENT_FORM_FIELD.counterparties,
										value,
										{
											shouldDirty: true,
										}
									)
								}
								disabled={isSaving}
								showWitnessCheckbox={counterpartyWitnessAvailable}
							/>
						)}
					</Column>
					{/* Everything that makes one recipient's copy differ from the next: the
					    schedule attached to it, the date stamped on it after signing, how it is
					    tagged, and the data points bound into it. Delivery closes the column
					    under its own rule. */}
					<Column>
						<Checkbox
							data-testid={`send-documents-individual-schedule-${template.id}`}
							name={MASTER_DOCUMENT_FORM_FIELD.individualScheduleRequired}
							control={form.control}
							label={tMipManage('send_documents.row_editor.individual_schedule')}
							disabled={isSaving || individualScheduleDisabled}
						/>
						{/* A Word template is re-laid out at generation, so there is no canvas to
						    place the date box on. */}
						{!isDocx && (
							<MantineCheckbox
								data-testid={`send-documents-post-dating-${template.id}`}
								label={tMipManage('master_documents.text.post_dating')}
								checked={postDatingEnabled}
								disabled={isSaving}
								onChange={(event) =>
									form.setValue(
										MASTER_DOCUMENT_FORM_FIELD.requiresPostDating,
										event.currentTarget.checked,
										{ shouldDirty: true }
									)
								}
							/>
						)}
						<MultiSelect
							label={tMipManage('send_documents.row_editor.category_tags')}
							data={classifierData}
							value={classifiersValue}
							onChange={(value) =>
								form.setValue(MASTER_DOCUMENT_FORM_FIELD.classifiers, value, {
									shouldDirty: true,
								})
							}
							searchable
							clearable
							disabled={isSaving}
						/>
						{/* A read-only document has nothing to place, so it gets no card rather
						    than a disabled one — Figma SDW/S3b shows none. */}
						{hasWork && (
							<DocumentBindingCard
								templateId={template.id}
								isDocx={isDocx}
								showFieldsTab={showFieldsTab}
								canPlaceSignaturesManually={canPlaceSignaturesManually}
								postDatingEnabled={postDatingEnabled}
								disabled={bindingDisabled}
								onOpen={() => setIsEditorOpen(true)}
							/>
						)}
						<ColumnSubSection>
							<ColumnSubSectionHeader>
								<Text fw={600}>
									{tMipManage('send_documents.row_editor.section.delivery')}
								</Text>
							</ColumnSubSectionHeader>
							<ColumnSubSectionBody>
								<Textarea
									name={MASTER_DOCUMENT_FORM_FIELD.instruction}
									control={form.control}
									label={tMipManage(
										'master_documents.text.instructions_to_investors'
									)}
									placeholder={tMipManage(
										'master_documents.text.instruction_placeholder'
									)}
									autosize
									minRows={4}
									disabled={isSaving}
								/>
							</ColumnSubSectionBody>
						</ColumnSubSection>
					</Column>
				</ColumnsGrid>
				<FooterBar>
					{/* liveDocumentCount, not issuedDocumentCount: the latter keeps terminal rows so
					    the Already sent column can answer "ever sent", and an older revoke left
					    instances CANCELLED rather than deleted — counting those as live
					    overstated this warning on any long-lived document. */}
					{template.liveDocumentCount > 0 && (
						<WarningBanner>
							<Text size="sm" c="dark.0">
								{tMipManage(
									template.liveDocumentCount === 1
										? 'send_documents.row_editor.live_workflows_one'
										: 'send_documents.row_editor.live_workflows_other',
									{ count: template.liveDocumentCount }
								)}
							</Text>
						</WarningBanner>
					)}
					<Button
						data-testid="send-documents-save-master"
						onClick={() => void handleSave()}
						loading={isSaving}
					>
						{tMipManage('send_documents.row_editor.save')}
					</Button>
				</FooterBar>
			</EditorPanel>
			<DocumentEditorModal
				opened={isEditorOpen}
				onClose={closeEditor}
				title={tMipManage('master_documents.field_binding.modal_title')}
			>
				{templateFile &&
					(isDocx ? (
						<DealFieldBindingEditor
							templateId={template.id}
							file={templateFile}
							sourceFormat={template.sourceFormat}
							panelHeader={documentNameField}
							panelHeaderEdit={documentNameEdit}
							onClose={closeEditor}
						/>
					) : (
						<DealPdfDocumentEditor
							templateId={template.id}
							file={templateFile}
							panelHeader={documentNameField}
							panelHeaderEdit={documentNameEdit}
							initialShowStampMetadata={
								form.getValues(MASTER_DOCUMENT_FORM_FIELD.showStampMetadata) ?? true
							}
							manualPlacementEnabled={canPlaceSignaturesManually}
							postDatingEnabled={postDatingEnabled}
							showFieldsTab={showFieldsTab}
							showSignaturesTab={showSignaturesTab}
							onClose={closeEditor}
							{...(placementLockReason === undefined
								? {}
								: { blockedReason: formatEnumValue(placementLockReason) })}
						/>
					))}
			</DocumentEditorModal>
		</RowEditorContainer>
	);
};
